// PowerSync tables and row parsing derived from the zod schemas every app already imports,
// so no app hand-writes column lists or 0/1 → boolean fixes.
import { Schema, Table, column, type BaseColumnType } from "@powersync/web";
import { z } from "zod";
import { TABLES, type TableName } from "./powersync-tables.js";

type Kind = "text" | "int" | "real" | "bool" | "json";

function kindOf(t: z.ZodTypeAny): Kind {
  while (t instanceof z.ZodDefault || t instanceof z.ZodNullable || t instanceof z.ZodOptional || t instanceof z.ZodEffects) {
    t = t instanceof z.ZodEffects ? t.innerType() : t._def.innerType;
  }
  if (t instanceof z.ZodBoolean) return "bool";
  if (t instanceof z.ZodNumber) return t.isInt ? "int" : "real";
  // A choice of whole numbers (stores.currency_precision is 0, 2 or 3): read back as a number, not as text.
  if (t instanceof z.ZodUnion && (t.options as z.ZodTypeAny[]).every((o) => o instanceof z.ZodLiteral && Number.isInteger(o.value))) return "int";
  if (t instanceof z.ZodArray || t instanceof z.ZodRecord || t instanceof z.ZodObject) return "json";
  return "text";
}

const kinds = new WeakMap<z.AnyZodObject, Record<string, Kind>>();
function kindsOf(schema: z.AnyZodObject): Record<string, Kind> {
  let k = kinds.get(schema);
  if (!k) kinds.set(schema, (k = Object.fromEntries(Object.entries(schema.shape).map(([name, t]) => [name, kindOf(t as z.ZodTypeAny)]))));
  return k;
}

const COLUMN: Record<Kind, BaseColumnType<string | number | null>> = { text: column.text, json: column.text, int: column.integer, bool: column.integer, real: column.real };

/** A PowerSync table with every column of a zod row schema (PowerSync adds `id` itself). */
export function psTable(schema: z.AnyZodObject, options?: ConstructorParameters<typeof Table>[1]): Table {
  const cols = Object.fromEntries(Object.entries(kindsOf(schema)).filter(([name]) => name !== "id").map(([name, k]) => [name, COLUMN[k]]));
  return new Table(cols, options);
}

// Postgres timestamps arrive as "2026-09-28 10:00:00Z"; Safari's Date can't parse the space.
const PG_TS = /^\d{4}-\d{2}-\d{2} \d/;

/** A SQLite row back into the shape the zod type promises: 0/1 → boolean, JSON text → arrays/objects, numeric text → number. */
export function fromRow<S extends z.AnyZodObject>(schema: S, row: Record<string, unknown>): z.infer<S> {
  const k = kindsOf(schema);
  const out: Record<string, unknown> = { ...row };
  for (const name in out) {
    const v = out[name];
    if (v == null) continue;
    switch (k[name]) {
      case "bool":
        out[name] = v === 1 || v === "1" || v === true || v === "true";
        break;
      case "int":
      case "real":
        out[name] = Number(v);
        break;
      case "json":
        if (typeof v === "string") out[name] = JSON.parse(v);
        break;
      default:
        if (typeof v === "string" && PG_TS.test(v)) out[name] = v.replace(" ", "T");
    }
  }
  return out as z.infer<S>;
}

/** A local change back into what sync-api's zod expects: 0/1 → boolean, JSON text → arrays/objects. */
export function toApi(schema: z.AnyZodObject, data: Record<string, unknown>): Record<string, unknown> {
  const k = kindsOf(schema);
  const out: Record<string, unknown> = { ...data };
  for (const name in out) {
    const v = out[name];
    if (v == null) continue;
    if (k[name] === "bool") out[name] = v === 1 || v === "1" || v === true;
    else if (k[name] === "json" && typeof v === "string") out[name] = JSON.parse(v);
  }
  return out;
}

/** The PowerSync schema for an app: the synced tables it reads, plus any local-only ones. */
export function psSchema(names: TableName[], extra: Record<string, Table> = {}): Schema {
  return new Schema({ ...Object.fromEntries(names.map((n) => [n, psTable(TABLES[n][0])])), ...extra });
}
